import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FadeIn } from './FadeIn';

const principles = [
  {
    number: "01",
    title: "Strategy Precedes Design",
    desc: "A beautiful brand applied to a confused strategy is just expensive decoration. We define the category, the enemy, and the unique mechanism before we talk about colors and type.",
    expanded: {
      detail: "Every engagement starts with a strategic deep-dive — not a mood board. We map your competitive landscape, challenge what you think you know about your market, and pressure-test your thesis against reality. Only after this clarity is established do we touch execution.",
      outcome: "You leave with a positioning document that becomes the single source of truth for every decision your brand makes — from hiring to product to sales narratives.",
      cta: "Explore a Strategy Engagement",
      mailSubject: "Strategy Engagement Inquiry",
      mailBody: "Hi Lee,\n\nI'm interested in exploring a strategy engagement with CHRSMA. I'd like to discuss how strategic positioning could sharpen our brand's competitive edge.\n\nA bit about us:\n- Company: \n- Stage: \n- Key challenge: \n\nLooking forward to connecting."
    }
  },
  {
    number: "02",
    title: "Operators Beat Theorists",
    desc: "We don't provide academic frameworks that die in Google Drive. Our positioning is built to be deployed by sales teams, marketing departments, and founders immediately.",
    expanded: {
      detail: "Lee has built, scaled, and operated companies in SaaS, marketplaces, and B2B platforms. That background means every recommendation comes with an execution plan. We know what a sales team actually needs to close, what a founder needs to fundraise, and what a marketing team needs to ship.",
      outcome: "Your positioning isn't a PDF — it's a weapon. Sales decks, pitch narratives, onboarding scripts, and messaging frameworks that your team can deploy the day we deliver.",
      cta: "See How We Operate",
      mailSubject: "Operator-Led Positioning Inquiry",
      mailBody: "Hi Lee,\n\nI came across CHRSMA and your operator-first approach resonates. We need positioning that our team can actually deploy — not another strategy deck.\n\nA bit about us:\n- Company: \n- Team size: \n- What we're trying to ship: \n\nWould love to discuss."
    }
  },
  {
    number: "03",
    title: "Simplicity Scales",
    desc: "Complexity is the enemy of execution. The best positioning is often the most obvious, distilled down to its absolute simplest form. If a 10-year-old can't understand it, it's too complex.",
    expanded: {
      detail: "We ruthlessly edit. Most brands try to say too much to too many people. We help you say one thing, to the right people, in a way that's impossible to misunderstand. This clarity compounds — it makes hiring easier, sales faster, and marketing cheaper.",
      outcome: "A single sentence that captures your entire brand thesis. A narrative so clear that every employee can repeat it without notes.",
      cta: "Simplify Your Brand",
      mailSubject: "Brand Simplification Inquiry",
      mailBody: "Hi Lee,\n\nWe're saying too much to too many people and it's hurting us. I'm interested in CHRSMA's approach to distilling positioning down to its simplest form.\n\nA bit about us:\n- Company: \n- Current positioning challenge: \n- What's not working: \n\nLet's talk."
    }
  }
];

export const Thesis = () => {
  const [activeCard, setActiveCard] = useState(null);

  return (
    <section id="thesis" className="relative py-32 md:py-48 bg-c-dark text-c-cream noise-overlay glow-top">
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-8 mb-24 md:mb-32">
          <div className="lg:col-span-4">
            <FadeIn>
              <h2 className="text-xs font-medium tracking-super-wide uppercase text-c-powder mb-6">How We Think</h2>
            </FadeIn>
          </div>
          <div className="lg:col-span-8">
            <FadeIn delay={0.1}>
              <h3 className="text-4xl md:text-6xl font-light">Our <span className="font-serif-italic text-gradient">Thesis</span></h3>
            </FadeIn>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 lg:gap-10">
          {principles.map((principle, index) => (
            <FadeIn key={index} delay={index * 0.1}>
              <button
                onClick={() => setActiveCard(index)}
                className="text-left w-full h-full rounded-xl border border-c-navy-light/40 bg-c-navy/20 p-6 md:p-10
                  hover:border-transparent hover:shadow-glow-mauve transition-all duration-500 gradient-border
                  cursor-pointer group"
              >
                <span className="font-serif-italic text-xl md:text-2xl mb-3 md:mb-6 block text-gradient">{principle.number}</span>
                <h4 className="text-lg md:text-2xl font-medium mb-3 md:mb-6 text-white">{principle.title}</h4>
                <p className="text-sm md:text-base text-c-powder/70 leading-relaxed font-light mb-4 md:mb-6">{principle.desc}</p>
                <span className="text-[10px] font-medium tracking-widest uppercase text-c-powder/60 group-hover:text-c-coral/70 transition-colors">
                  Learn more
                </span>
              </button>
            </FadeIn>
          ))}
        </div>
      </div>

      {/* Modal overlay */}
      <AnimatePresence>
        {activeCard !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-6"
            onClick={() => setActiveCard(null)}
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-c-dark/90 backdrop-blur-sm" />

            {/* Modal content */}
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.98 }}
              transition={{ duration: 0.4, ease: [0.21, 0.47, 0.32, 0.98] }}
              className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-xl border border-c-navy-light/40 bg-c-navy/80 backdrop-blur-xl p-6 md:p-14 shadow-glow-mauve gradient-border"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={() => setActiveCard(null)}
                className="absolute top-6 right-6 text-c-powder/60 hover:text-white transition-colors"
                aria-label="Close"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <line x1="4" y1="4" x2="16" y2="16" />
                  <line x1="16" y1="4" x2="4" y2="16" />
                </svg>
              </button>

              <span className="font-serif-italic text-2xl md:text-3xl mb-3 block text-gradient">
                {principles[activeCard].number}
              </span>
              <h3 className="text-xl md:text-3xl font-medium text-white mb-4 md:mb-6 pr-8">
                {principles[activeCard].title}
              </h3>

              <div className="space-y-4 md:space-y-6 mb-6 md:mb-10">
                <p className="text-sm md:text-base text-c-powder/80 font-light leading-relaxed">
                  {principles[activeCard].expanded.detail}
                </p>
                <div className="border-l-2 border-c-mauve/40 pl-4 md:pl-6">
                  <p className="text-[10px] font-medium tracking-widest uppercase text-c-powder/50 mb-1.5">The Outcome</p>
                  <p className="text-sm md:text-base text-c-cream/80 font-light leading-relaxed">
                    {principles[activeCard].expanded.outcome}
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => {
                    setActiveCard(null);
                    setTimeout(() => {
                      const el = document.getElementById('contact');
                      if (el) { const offset = window.innerWidth < 768 ? 64 : 80; window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - offset, behavior: 'smooth' }); }
                    }, 300);
                  }}
                  className="inline-flex items-center justify-center text-[11px] font-medium tracking-widest uppercase px-6 py-3 rounded-xl cta-wire"
                >
                  {principles[activeCard].expanded.cta}
                </button>
                <a
                  href={`mailto:hello@chrsma.co?subject=${encodeURIComponent(principles[activeCard].expanded.mailSubject)}&body=${encodeURIComponent(principles[activeCard].expanded.mailBody)}`}
                  className="inline-flex items-center justify-center gap-2 text-[11px] font-medium tracking-widest uppercase text-c-powder/60 hover:text-white px-6 py-3 transition-colors"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="M22 4L12 13L2 4" />
                  </svg>
                  Email Directly
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
